/** Human-friendly formatting helpers used across the lab. */

const NF = new Intl.NumberFormat("en-US");

/** 1234567 → "1,234,567". */
export function formatNumber(n: number): string {
  return NF.format(Math.round(n));
}

/** 1500 → "1.5K", 2_000_000 → "2M", 3.2e9 → "3.2B". */
export function formatCount(n: number): string {
  const abs = Math.abs(n);
  if (abs < 1000) return String(Math.round(n));
  const units = [
    { v: 1e12, s: "T" },
    { v: 1e9, s: "B" },
    { v: 1e6, s: "M" },
    { v: 1e3, s: "K" },
  ];
  for (const u of units) {
    if (abs >= u.v) {
      const scaled = n / u.v;
      const str = scaled >= 100 ? scaled.toFixed(0) : scaled.toFixed(1).replace(/\.0$/, "");
      return str + u.s;
    }
  }
  return String(n);
}

/** Base-1024 bytes → "12 KB", "3.4 MB". */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i++;
  }
  const str = value >= 100 ? value.toFixed(0) : value.toFixed(1).replace(/\.0$/, "");
  return `${str} ${units[i]}`;
}

/** 0.0081 → "0.81%". Uses enough significant digits for tiny rates. */
export function formatPercent(fraction: number, maxDigits = 2): string {
  const pct = fraction * 100;
  if (pct === 0) return "0%";
  if (pct < 0.01) return `${pct.toExponential(1)}%`;
  const digits = pct < 1 ? Math.max(maxDigits, 2) : maxDigits;
  return `${Number(pct.toFixed(digits))}%`;
}

/** A ratio like 0.0009766 → "1 in 1,024". */
export function formatOneIn(fraction: number): string {
  if (fraction <= 0) return "never";
  return `1 in ${formatNumber(1 / fraction)}`;
}
